import { FlexColumn, FlexRowAlignCenter } from "@/components/Flex";
import TabSwitcher, { TabProps, TabSwitcherProps } from "@/components/TabSwitcher";
import { useSafeQueryNavigation } from "@/hooks/navigation/useSafeQueryNavigation";
import br from "@/styles/breakpoints.module.css";
import Mixpanel from "@/utils/analytics/Mixpanel";
import { dynamicKeyValueStore } from "@/utils/dynamicKeyValueStore";
import { spacing } from "@/utils/spacing";
import { useSyncedState } from "@knowt/syncing/utils/hooks/useSyncedState";
import Fade from "@mui/material/Fade";
import clsx from "clsx";
import { usePathname, useSearchParams } from "next/navigation";
import React, { ComponentProps, useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";

const TabbedViewContext = React.createContext<{
    currentTab: string;
    changeTab: (tab: string) => void;
} | null>(null);

export const TabPanel = ({
    children,
    mixpanelProps,
    onActive,
    isActive,
}: {
    children: React.ReactNode;
    keepMounted?: boolean;
    mixpanelProps?: {
        event: string;
        props: any;
    };
    onActive?: () => void | Promise<void>;
    isActive?: boolean;
} & TabProps) => {
    useEffect(() => {
        if (isActive) {
            if (mixpanelProps) {
                Mixpanel.track(mixpanelProps.event, mixpanelProps.props);
            }
            onActive?.();
        }
    }, [isActive, mixpanelProps, onActive]);

    return children;
};

TabPanel.displayName = "TabPanel";

const findAllTabPanels = (children, parentIndex = 0) => {
    const result: TabProps[] = [];

    React.Children.forEach(
        children,
        (
            child: {
                type: { displayName: string };
                props: {
                    children: React.ReactNode;
                    value: number;
                    label: string;
                    disabled: boolean;
                    disabledToast: string;
                    onActive?: () => void | Promise<void>;
                };
            },
            index
        ) => {
            if (React.isValidElement(child)) {
                if (child.type.displayName === TabPanel.displayName) {
                    result.push({ ...child.props });
                } else if (child.props?.children) {
                    const childPanels = findAllTabPanels(child.props.children, parentIndex + index);
                    result.push(...childPanels);
                }
            }
        }
    );

    return result;
};

const TabbedView = ({
    children,
    tabSwitcherProps,
    headerRightComponent,
    headerContainerProps,
    headerLeftComponent,
    fadeProps,
    disableUrlSync: _disableUrlSync,
    disableFade,
    disableSavingTab,
    syncedTab,
    onSyncedTabChange,
    ...props
}: {
    children:
        | any
        | React.ReactElement<any>
        | React.ReactElement<any>[]
        | undefined
        | (React.ReactElement<any> | undefined | React.ReactElement<any>[])[];
    headerRightComponent?: React.ReactNode;
    headerContainerProps?: React.HTMLAttributes<HTMLDivElement>;
    headerLeftComponent?: React.ReactNode;
    tabSwitcherProps?: Omit<TabSwitcherProps, "tabs" | "selectedTab" | "setSelectedTab">;
    fadeProps?: Partial<ComponentProps<typeof Fade>>;
    disableUrlSync?: boolean;
    disableFade?: boolean;
    disableSavingTab?: boolean;
    syncedTab?: number;
    onSyncedTabChange?: (tab: number) => void;
} & React.HTMLAttributes<HTMLDivElement>) => {
    const disableUrlSync = _disableUrlSync || syncedTab !== undefined;
    const paths = usePathname();

    const { addParamsSilentReplace } = useSafeQueryNavigation();

    const tabFromSearchParams = decodeURIComponent(useSearchParams()?.get("tab") || "");

    const tabsFromTabPanels = useMemo(
        () => (findAllTabPanels(children) as TabProps[]).map((tab, index) => ({ ...tab, value: index })) || [],
        [children]
    );

    const TabbedViewId = `TabbedView-${(paths.split("/") || ["no-path"]).join("-")}-${
        tabsFromTabPanels.map(tab => tab.label).join("-") || "no-tabs"
    }`;

    const tabIndexFromSearchParams = useMemo(() => {
        return disableUrlSync ? 0 : tabsFromTabPanels.findIndex(tab => tab.label === tabFromSearchParams);
    }, [tabFromSearchParams, tabsFromTabPanels, disableUrlSync]);

    const defaultTab = useMemo(() => {
        return tabIndexFromSearchParams > 0
            ? tabsFromTabPanels[tabIndexFromSearchParams]?.disabled
                ? tabsFromTabPanels.findIndex(tab => !tab.disabled)
                : tabIndexFromSearchParams
            : !disableSavingTab
              ? Number.parseInt(dynamicKeyValueStore.get(TabbedViewId) || "0")
              : 0;
    }, [tabIndexFromSearchParams, tabsFromTabPanels, disableSavingTab, TabbedViewId]);

    const [selectedTab, setSelectedTab] = useSyncedState<number>(syncedTab || defaultTab || 0);
    const [isReadyForFade, setIsReadyForFade] = useState(false);

    const isDefaultChecksDone = useRef(false);

    useEffect(() => {
        if (typeof window !== "undefined") {
            setIsReadyForFade(true);
        }
    }, []);

    useEffect(() => {
        // this is for when page loads firs
        if (!isDefaultChecksDone.current) {
            if (!disableUrlSync && !disableSavingTab)
                addParamsSilentReplace({
                    tab: tabsFromTabPanels[defaultTab]?.label,
                });
            isDefaultChecksDone.current = true;
        }
    }, [selectedTab, tabsFromTabPanels, addParamsSilentReplace, disableUrlSync, defaultTab, disableSavingTab]);

    useEffect(() => {
        // change selected tab in state when the searchParams one chang
        if (
            !disableUrlSync &&
            tabFromSearchParams &&
            selectedTab !== tabsFromTabPanels.findIndex(tab => tab.label === tabFromSearchParams) &&
            isDefaultChecksDone.current
        ) {
            const newTab = tabsFromTabPanels.findIndex(tab => tab.label === tabFromSearchParams);

            if (tabsFromTabPanels?.[newTab]?.disabled) {
                addParamsSilentReplace({ tab: tabsFromTabPanels[selectedTab]?.label });
                toast.error(tabsFromTabPanels[newTab]?.disabledToast || "This tab is disabled");
                return;
            }

            setSelectedTab(newTab);
        }
    }, [
        tabFromSearchParams,
        selectedTab,
        tabsFromTabPanels,
        addParamsSilentReplace,
        isDefaultChecksDone,
        disableUrlSync,
    ]);

    const changeTab = useCallback(
        (tab: string) => {
            const index = tabsFromTabPanels.findIndex(tabPanel => tabPanel.label === tab);
            if (index < 0) return;
            if (!disableUrlSync) addParamsSilentReplace({ tab: tabsFromTabPanels[index]?.label });
            if (!disableSavingTab) dynamicKeyValueStore.set(TabbedViewId, index.toString());
            onSyncedTabChange?.(index);
            setTimeout(() => setSelectedTab(tabsFromTabPanels[index]?.value || 0), !disableUrlSync ? 30 : 0);
        },
        [tabsFromTabPanels, disableUrlSync, addParamsSilentReplace, disableSavingTab, TabbedViewId, onSyncedTabChange]
    );

    const renderChild = (child, index) => {
        if (child && React.isValidElement(child) && child.type.displayName === TabPanel.displayName) {
            const isVisibleTab = tabsFromTabPanels[selectedTab]?.label === child.props.label;

            return (
                <Fade
                    key={index}
                    in={isVisibleTab}
                    appear={isReadyForFade}
                    timeout={disableFade ? 0 : { enter: 500, exit: 500 }}
                    unmountOnExit={!child.props.keepMounted}
                    {...fadeProps}>
                    <div
                        style={{
                            width: "100%",
                            height: "100%",
                            maxHeight: "100%",
                            maxWidth: "100%",
                            position: isVisibleTab ? "relative" : "absolute",
                            visibility: isVisibleTab ? "visible" : "hidden",
                        }}
                        data-testid="auto-id-b52d3688-0760-48bb-a483-48d6dad9e504">
                        {React.cloneElement(child, { isActive: isVisibleTab })}
                    </div>
                </Fade>
            );
        } else if (child?.props?.children) {
            return React.cloneElement(child, {
                ...child.props,
                children: React.Children.map(child.props.children, renderChild),
            });
        }

        return child;
    };

    return (
        <TabbedViewContext.Provider
            value={{
                currentTab: tabsFromTabPanels[selectedTab]?.label,
                changeTab,
            }}>
            <FlexColumn
                {...props}
                style={{
                    gap: spacing.MD,
                    ...props?.style,
                }}>
                <FlexRowAlignCenter {...headerContainerProps} style={{ width: "100%", ...headerContainerProps?.style }}>
                    {headerLeftComponent}
                    {tabsFromTabPanels.length > 1 && (
                        <>
                            <TabSwitcher
                                className={clsx(br.smDownDisplayNone, props.className)}
                                tabs={tabsFromTabPanels}
                                selectedTab={selectedTab}
                                setSelectedTab={index => {
                                    changeTab(tabsFromTabPanels[index]?.label);
                                }}
                                fontSize="1.6rem"
                                height="5.5rem"
                                {...tabSwitcherProps}
                                containerStyle={{ maxWidth: "fit-content", ...tabSwitcherProps?.containerStyle }}
                            />
                            {/* TODO: find a better way allow responsive design through the component call itself */}
                            <TabSwitcher
                                className={clsx(br.smUpDisplayNone)}
                                tabs={tabsFromTabPanels}
                                selectedTab={selectedTab}
                                setSelectedTab={index => {
                                    changeTab(tabsFromTabPanels[index]?.label);
                                }}
                                {...tabSwitcherProps}
                                width={"11rem"}
                                fontSize="1.3rem"
                                height="5.5rem"
                                containerStyle={{ maxWidth: "fit-content", ...tabSwitcherProps?.containerStyle }}
                            />
                        </>
                    )}
                    {headerRightComponent}
                </FlexRowAlignCenter>
                {React.Children.map(children, renderChild)}
            </FlexColumn>
        </TabbedViewContext.Provider>
    );
};

export default TabbedView;

export const useTabbedView = () => {
    const context = React.useContext(TabbedViewContext);

    if (!context) {
        throw new Error("useTabbedView must be used within a TabbedView");
    }

    return context;
};
